import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  DiscoverImapServerBodySchema,
  ImapProviderSchema,
  type DiscoverImapServerResponse,
  type ImapServer,
} from '@immo/api-contract';
import { Button, ImapProviderLogo, InfoBox, Input, Label, Modal, useToast } from '@immo/ui-kit';
import { FormField } from '@/components/form';
import { LockIcon, WarningTriangleIcon } from '@/components/layout/icons';
import { apiClient } from '@/lib/auth/api-client';
import { translateErrorCode } from '@/lib/errors';
import type { ImapReconnect } from '../mailbox-connect-flow';
import { useTranslations } from '@/i18n/use-translations';

export const DISCOVERY_DEBOUNCE_MS = 400;
export const DISCOVERY_DEADLINE_MS = 5_000;

const FORM_ID = 'imap-connect-form';
const DEFAULT_IMAP_PORT = '993';
const DEFAULT_SMTP_PORT = '465';
const MAX_PORT = 65535;
const UNKNOWN: DiscoverImapServerResponse = { provider: null, imap: null, smtp: null };

export type ImapConnectValues = {
  readonly email: string;
  readonly password: string;
  readonly username: string;
  readonly imap: ImapServer;
};

export type ImapConnectAnswer =
  | { readonly ok: true; readonly email: string }
  | { readonly ok: false; readonly code: string | undefined; readonly details: Record<string, unknown> | undefined };

// The submit belongs to the caller: the route-gate coverage scan reads every file a surface imports, so
// a step that called both the personal and the company route would make each surface ask for both permissions.
export type ImapConnectTarget =
  | {
      readonly purpose: 'personal';
      readonly connect: (values: ImapConnectValues & { readonly smtp: ImapServer }) => Promise<ImapConnectAnswer>;
    }
  | {
      readonly purpose: 'company';
      // A reconnect: the address is the stored one and cannot be edited.
      readonly emailLocked: boolean;
      readonly connect: (values: ImapConnectValues) => Promise<ImapConnectAnswer>;
    };

type ImapConnectStepProps = {
  readonly workspaceId: string;
  readonly target: ImapConnectTarget;
  readonly initial?: ImapReconnect | undefined;
  readonly onBack: () => void;
  readonly onClose: () => void;
  readonly onConnected: () => void;
};

type Servers = { imapHost: string; imapPort: string; smtpHost: string; smtpPort: string; username: string };
type Discovery = DiscoverImapServerResponse & { email: string };
type Seed = { email: string; servers: Servers; serversOpen: boolean; discovery: Discovery | null };
type ErrorKey =
  | 'required-email'
  | 'invalid-email'
  | 'required-password'
  | 'required-imap-host'
  | 'required-smtp-host'
  | 'invalid-port';
type Field = 'email' | 'password' | 'imapHost' | 'imapPort' | 'smtpHost' | 'smtpPort';
type Errors = Partial<Record<Field, ErrorKey>>;
type Mark = 'email' | 'imapHost' | 'imapPort' | 'smtpHost' | 'smtpPort';
type Refusal = { readonly message: string; readonly hint: string | null };
type Refused = { readonly mark: ReadonlyArray<Mark>; readonly openServers: boolean };

const NO_MARKS: ReadonlySet<Mark> = new Set();

const isWellFormed = (email: string) => DiscoverImapServerBodySchema.safeParse({ email }).success;
const isPort = (value: string) => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= MAX_PORT;
const domainOf = (email: string) => email.slice(email.lastIndexOf('@') + 1);

// A failure, a throw or the deadline all read as "not recognised" (BL-113): the
// member then types the server data, so discovery never blocks the step.
async function discoverImapServer(workspaceId: string, email: string): Promise<DiscoverImapServerResponse> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<DiscoverImapServerResponse>((resolve) => {
    timer = setTimeout(() => resolve(UNKNOWN), DISCOVERY_DEADLINE_MS);
  });
  const request = apiClient.mailbox
    .discoverImap({ pathParams: { workspaceId }, body: { email } })
    .then((result): DiscoverImapServerResponse => (result.ok ? result.body : UNKNOWN))
    .catch(() => UNKNOWN);

  try {
    return await Promise.race([request, deadline]);
  } finally {
    clearTimeout(timer);
  }
}

const serversOf = (answer: DiscoverImapServerResponse, email: string): Servers =>
  answer.imap !== null && answer.smtp !== null
    ? {
        imapHost: answer.imap.host,
        imapPort: String(answer.imap.port),
        smtpHost: answer.smtp.host,
        smtpPort: String(answer.smtp.port),
        username: email,
      }
    : { imapHost: '', imapPort: DEFAULT_IMAP_PORT, smtpHost: '', smtpPort: DEFAULT_SMTP_PORT, username: email };

// A reconnect starts from what the connection stored, with the provider it was recognised as, so no
// discovery runs for the kept address and stored server overrides survive.
function seedOf(initial: ImapReconnect | undefined): Seed {
  if (initial === undefined) {
    return { email: '', servers: serversOf(UNKNOWN, ''), serversOpen: false, discovery: null };
  }

  const { imap } = initial;

  return {
    email: initial.email,
    servers: {
      imapHost: imap.imap.host,
      imapPort: String(imap.imap.port),
      smtpHost: imap.smtp?.host ?? '',
      smtpPort: imap.smtp === null ? DEFAULT_SMTP_PORT : String(imap.smtp.port),
      username: imap.username,
    },
    serversOpen: imap.provider === null,
    discovery: { email: initial.email, provider: imap.provider, imap: imap.imap, smtp: imap.smtp },
  };
}

// The field at fault per refusal (AC 12–16): `details.server` names the server that failed.
function refusedFields(code: string | undefined, details: Record<string, unknown> | undefined): Refused {
  const server = details?.['server'] === 'SMTP' ? 'smtp' : 'imap';

  switch (code) {
    case 'communications.imap-mailbox.server-unreachable':
      return { mark: [`${server}Host`, `${server}Port`], openServers: true };
    case 'communications.imap-mailbox.sending-refused':
      return { mark: ['smtpHost'], openServers: true };
    case 'communications.imap-mailbox.unencrypted':
      return { mark: [`${server}Port`], openServers: true };
    case 'communications.mailbox.connected-as-company-mailbox':
    case 'communications.company-mailbox.already-connected':
    case 'communications.company-mailbox.connected-as-personal':
      return { mark: ['email'], openServers: false };
    default:
      return { mark: [], openServers: false };
  }
}

export function ImapConnectStep({ workspaceId, target, initial, onBack, onClose, onConnected }: ImapConnectStepProps) {
  const t = useTranslations('pages.settings.mailbox.imap');
  const tMailbox = useTranslations('pages.settings.mailbox');
  const company = target.purpose === 'company';
  const locked = target.purpose === 'company' && target.emailLocked;
  const tErrors = useTranslations();
  const toast = useToast();
  const [seed] = useState(() => seedOf(initial));
  const [email, setEmail] = useState(seed.email);
  const [password, setPassword] = useState('');
  const [servers, setServers] = useState<Servers>(seed.servers);
  const [serversOpen, setServersOpen] = useState(seed.serversOpen);
  const [discovery, setDiscovery] = useState<Discovery | null>(seed.discovery);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [marked, setMarked] = useState<ReadonlySet<Mark>>(NO_MARKS);
  // Esc, the X and the backdrop ask „Änderungen verwerfen?“ only once the member typed something.
  const [touched, setTouched] = useState(false);

  // The refs mirror state that an answer landing later (or the submit that awaits
  // it) must read fresh, never from the closure it was started in.
  const emailRef = useRef(seed.email);
  const serversRef = useRef(seed.servers);
  const serversOpenRef = useRef(seed.serversOpen);
  const serversEditedRef = useRef(false);
  const discoveryRef = useRef<Discovery | null>(seed.discovery);
  const inFlight = useRef(new Map<string, Promise<void>>());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const address = email.trim();

  const commitServers = useCallback((next: Servers, open: boolean) => {
    serversRef.current = next;
    serversOpenRef.current = open;
    setServers(next);
    setServersOpen(open);
  }, []);

  const apply = useCallback(
    (requested: string, answer: DiscoverImapServerResponse) => {
      if (emailRef.current !== requested) return;

      const next = { ...answer, email: requested };
      discoveryRef.current = next;
      setDiscovery(next);

      if (serversEditedRef.current) {
        if (answer.provider === null) commitServers(serversRef.current, true);

        return;
      }

      commitServers(serversOf(answer, requested), answer.provider === null);
    },
    [commitServers],
  );

  const discover = useCallback(
    (requested: string): Promise<void> => {
      if (discoveryRef.current?.email === requested) return Promise.resolve();

      const pending = inFlight.current.get(requested);

      if (pending !== undefined) return pending;

      const request = discoverImapServer(workspaceId, requested).then((answer) => {
        inFlight.current.delete(requested);
        apply(requested, answer);
      });
      inFlight.current.set(requested, request);

      return request;
    },
    [workspaceId, apply],
  );

  useEffect(() => {
    if (!isWellFormed(address)) return;

    debounceTimer.current = setTimeout(() => void discover(address), DISCOVERY_DEBOUNCE_MS);

    return () => clearTimeout(debounceTimer.current);
  }, [address, discover]);

  const clearError = (field: Field) => setErrors((previous) => ({ ...previous, [field]: undefined }));

  const clearMark = (mark: Mark) =>
    setMarked((previous) => {
      if (!previous.has(mark)) return previous;

      const next = new Set(previous);
      next.delete(mark);

      return next;
    });

  const changeEmail = (value: string) => {
    setTouched(true);
    emailRef.current = value.trim();
    setEmail(value);
    clearError('email');
    clearMark('email');
  };

  const changeServer = (field: keyof Servers, value: string) => {
    setTouched(true);
    serversEditedRef.current = true;
    commitServers({ ...serversRef.current, [field]: value }, serversOpenRef.current);

    if (field !== 'username') {
      clearError(field);
      clearMark(field);
    }
  };

  const blurEmail = () => {
    clearTimeout(debounceTimer.current);

    if (isWellFormed(address)) void discover(address);
  };

  // Rendered from the error CODE, never from `error.message` (ADR 0014). A code the dictionary does
  // not know reads as the generic copy.
  const refuse = (code: string | undefined, details: Record<string, unknown> | undefined) => {
    const fields = refusedFields(code, details);

    if (fields.openServers) commitServers(serversRef.current, true);

    setMarked(new Set(fields.mark));
    setRefusal({
      message: translateErrorCode(tErrors, code === undefined ? undefined : `errors.${code}`, details),
      hint: code === 'communications.imap-mailbox.login-refused' ? hintFor(details?.['provider']) : null,
    });
  };

  const hintFor = (reported: unknown): string | null => {
    const provider = ImapProviderSchema.safeParse(reported).data ?? discoveryRef.current?.provider ?? null;

    switch (provider) {
      case 'GMX':
      case 'WEB_DE':
        return t('hint.gmx-web-de', { provider: t(`providers.${provider}`) });
      case 'T_ONLINE':
        return t('hint.t-online');
      case 'FREENET':
        return t('hint.freenet');
      default:
        return null;
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (submitting) return;

    const next: Errors = {};

    if (address === '') next.email = 'required-email';
    else if (!isWellFormed(address)) next.email = 'invalid-email';

    if (password === '') next.password = 'required-password';

    if (next.email === undefined) {
      clearTimeout(debounceTimer.current);
      await discover(address);

      if (emailRef.current !== address) return;
    }

    if (serversOpenRef.current) {
      const current = serversRef.current;

      if (current.imapHost.trim() === '') next.imapHost = 'required-imap-host';

      if (!company && current.smtpHost.trim() === '') next.smtpHost = 'required-smtp-host';

      if (!isPort(current.imapPort.trim())) next.imapPort = 'invalid-port';

      if (!company && !isPort(current.smtpPort.trim())) next.smtpPort = 'invalid-port';
    }

    setErrors(next);

    if (Object.keys(next).length > 0) return;

    const current = serversRef.current;

    setRefusal(null);
    setMarked(NO_MARKS);
    setSubmitting(true);

    try {
      const values: ImapConnectValues = {
        email: address,
        password,
        username: current.username.trim() === '' ? address : current.username.trim(),
        imap: { host: current.imapHost.trim(), port: Number(current.imapPort.trim()) },
      };
      const answer =
        target.purpose === 'personal'
          ? await target.connect({
              ...values,
              smtp: { host: current.smtpHost.trim(), port: Number(current.smtpPort.trim()) },
            })
          : await target.connect(values);

      if (answer.ok) {
        if (target.purpose === 'personal') toast.success(tMailbox('connect-success', { email: answer.email }));

        onConnected();

        return;
      }

      refuse(answer.code, answer.details);
    } catch {
      setRefusal({ message: tErrors('errors.generic.unexpected'), hint: null });
    } finally {
      setSubmitting(false);
    }
  };

  const current = discovery !== null && discovery.email === address ? discovery : null;
  const recognised = current?.provider ?? null;

  return (
    <Modal
      open
      onClose={onClose}
      dirty={touched}
      size="md"
      title={t(company ? 'title-company' : 'title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onBack} disabled={submitting}>
            {t('back')}
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={submitting}>
            {submitting ? t('checking') : t('connect')}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={(event) => void submit(event)} className="space-y-3">
        {refusal !== null && (
          <InfoBox type="danger" icon={<WarningTriangleIcon className="h-4 w-4" />}>
            <p>{refusal.message}</p>
            {refusal.hint !== null && <p className="mt-1">{refusal.hint}</p>}
          </InfoBox>
        )}
        <FormField id="imap-email" label={t('email')} error={errors.email === undefined ? null : t(errors.email)}>
          {(errId) => (
            <Input
              id="imap-email"
              type="email"
              autoComplete="email"
              value={email}
              placeholder={t('email-placeholder')}
              readOnly={submitting || locked}
              invalid={errId !== undefined || marked.has('email')}
              aria-describedby={errId}
              onChange={(event) => changeEmail(event.target.value)}
              onBlur={blurEmail}
            />
          )}
        </FormField>

        {recognised !== null && (
          <p className="flex items-center gap-2 text-sm text-text-secondary">
            <ImapProviderLogo provider={recognised} size={16} />
            {t('recognised', { provider: t(`providers.${recognised}`) })}
            {!serversOpen && (
              <Button
                variant="link"
                type="button"
                disabled={submitting}
                onClick={() => commitServers(serversRef.current, true)}
              >
                {t('edit-servers')}
              </Button>
            )}
          </p>
        )}

        <FormField
          id="imap-password"
          label={t('password')}
          helper={t('password-hint')}
          error={errors.password === undefined ? null : t(errors.password)}
        >
          {(errId) => (
            <Input
              id="imap-password"
              type="password"
              autoComplete="current-password"
              value={password}
              readOnly={submitting}
              invalid={errId !== undefined}
              aria-describedby={errId}
              onChange={(event) => {
                setTouched(true);
                setPassword(event.target.value);
                clearError('password');
              }}
            />
          )}
        </FormField>

        {serversOpen && (
          <div className="space-y-3 rounded-md bg-subtle p-3">
            {current !== null && recognised === null && (
              <p className="text-sm text-text-secondary">{t('unknown-intro', { domain: domainOf(address) })}</p>
            )}
            <ServerRow
              id="imap"
              hostLabel={t('imap-host')}
              hostPlaceholder={t('imap-host-placeholder')}
              portLabel={t('port')}
              host={servers.imapHost}
              port={servers.imapPort}
              readOnly={submitting}
              hostMarked={marked.has('imapHost')}
              portMarked={marked.has('imapPort')}
              hostError={errors.imapHost === undefined ? null : t(errors.imapHost)}
              portError={errors.imapPort === undefined ? null : t(errors.imapPort)}
              onHost={(value) => changeServer('imapHost', value)}
              onPort={(value) => changeServer('imapPort', value)}
            />
            {!company && (
              <ServerRow
                id="smtp"
                hostLabel={t('smtp-host')}
                hostPlaceholder={t('smtp-host-placeholder')}
                portLabel={t('port')}
                host={servers.smtpHost}
                port={servers.smtpPort}
                readOnly={submitting}
                hostMarked={marked.has('smtpHost')}
                portMarked={marked.has('smtpPort')}
                hostError={errors.smtpHost === undefined ? null : t(errors.smtpHost)}
                portError={errors.smtpPort === undefined ? null : t(errors.smtpPort)}
                onHost={(value) => changeServer('smtpHost', value)}
                onPort={(value) => changeServer('smtpPort', value)}
              />
            )}
            <FormField id="imap-username" label={t('username')} error={null}>
              {() => (
                <Input
                  id="imap-username"
                  autoComplete="username"
                  value={servers.username}
                  readOnly={submitting}
                  onChange={(event) => changeServer('username', event.target.value)}
                />
              )}
            </FormField>
          </div>
        )}

        <p className="flex items-start gap-2 text-xs text-text-tertiary">
          <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {t(company ? 'security-note-company' : 'security-note')}
        </p>
      </form>
    </Modal>
  );
}

type ServerRowProps = {
  readonly id: 'imap' | 'smtp';
  readonly hostLabel: string;
  readonly hostPlaceholder: string;
  readonly portLabel: string;
  readonly host: string;
  readonly port: string;
  readonly readOnly: boolean;
  readonly hostMarked: boolean;
  readonly portMarked: boolean;
  readonly hostError: string | null;
  readonly portError: string | null;
  readonly onHost: (value: string) => void;
  readonly onPort: (value: string) => void;
};

function ServerRow({
  id,
  hostLabel,
  hostPlaceholder,
  portLabel,
  host,
  port,
  readOnly,
  hostMarked,
  portMarked,
  hostError,
  portError,
  onHost,
  onPort,
}: ServerRowProps) {
  const portErrorId = `imap-${id}-port-error`;

  return (
    <div className="space-y-1">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <FormField id={`imap-${id}-host`} label={hostLabel} error={hostError}>
            {(errId) => (
              <Input
                id={`imap-${id}-host`}
                value={host}
                placeholder={hostPlaceholder}
                autoComplete="off"
                readOnly={readOnly}
                invalid={errId !== undefined || hostMarked}
                aria-describedby={errId}
                onChange={(event) => onHost(event.target.value)}
              />
            )}
          </FormField>
        </div>
        <div className="w-24 shrink-0 space-y-1">
          <Label htmlFor={`imap-${id}-port`}>{portLabel}</Label>
          <Input
            id={`imap-${id}-port`}
            inputMode="numeric"
            autoComplete="off"
            aria-label={`${hostLabel}, ${portLabel}`}
            value={port}
            readOnly={readOnly}
            invalid={portError !== null || portMarked}
            aria-describedby={portError === null ? undefined : portErrorId}
            onChange={(event) => onPort(event.target.value)}
          />
        </div>
      </div>
      {portError !== null && (
        <p id={portErrorId} role="alert" className="text-sm text-danger-600">
          {portError}
        </p>
      )}
    </div>
  );
}
